import { Switch } from '@mui/material';
import { AxiosError } from 'axios';
import React, { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UseQueryResult } from 'react-query';

import { telegramFlowApi } from 'api';
import {
  CheckTelegramChatMembershipIcon,
  CrudPage,
  CrudTableActionType,
  CrudTableAction,
  DataGridColumnDefinition,
  CloseFormikDialogData,
  dataGridColumns,
} from 'components';
import { useMutation } from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  FilterDefinition,
  PaginatedData,
  TelegramFlow,
  TelegramUser,
} from 'types';
import { validationUtils } from 'utils';

import { TelegramFlowDialog, TelegramFlowContext } from './TelegramFlowDialog';
import { TelegramFlowFilters } from './types';

type TelegramFlowTabProps = {
  filtersDefinitions: FilterDefinition<TelegramFlowFilters>[];
  entityColumn: DataGridColumnDefinition<TelegramFlow>;
  queryResult: UseQueryResult<PaginatedData<TelegramFlow>>;
  context: TelegramFlowContext;
  telegramUsers: TelegramUser[];
  additionalColumns?: DataGridColumnDefinition<TelegramFlow>[];
};

export const TelegramFlowTab: React.FC<TelegramFlowTabProps> = ({
  filtersDefinitions,
  entityColumn,
  queryResult,
  context,
  telegramUsers,
  additionalColumns,
}) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'features.telegram_flow',
  });
  const { mutate: remove } = useMutation(telegramFlowApi.remove, {
    onSuccess: () => {
      queryResult.refetch();
    },
    notifierType: 'remove',
  });

  const { mutate: update } = useMutation<
    any,
    AxiosError,
    { id: string; data: any }
  >(telegramFlowApi.update, {
    onSuccess: () => queryResult.refetch(),
  });

  const handleUpdate = useCallback(
    (
      item: TelegramFlow,
      data: CloseFormikDialogData<any>,
      closeDialog: () => void,
    ) => {
      update(
        { id: item.id, data: data?.values },
        {
          onSuccess: () => {
            closeDialog();
          },
          onError: (error: AxiosError) => {
            data?.formikHelpers.setErrors(validationUtils.getFormErrors(error));
          },
        },
      );
    },
    [update],
  );

  const handleUpdateEnabled = useCallback(
    (item: TelegramFlow) => {
      update({ id: item.id, data: { enabled: !item.enabled } });
    },
    [update],
  );

  const actions: CrudTableAction[] = useMemo(
    () => [
      {
        type: CrudTableActionType.Details,
        renderDialog: (props) => (
          <TelegramFlowDialog
            open={props.open}
            onClose={props.onClose}
            context={context}
            data={props.data}
            telegramUsers={telegramUsers}
          />
        ),
        onUpdate: handleUpdate,
      },
      {
        type: CrudTableActionType.Remove,
        onRemove: (item: any, { close }: any) =>
          remove(item.id, { onSuccess: close }),
      },
    ],
    [context, handleUpdate, remove, telegramUsers],
  );

  const columns = useMemo(
    (): DataGridColumnDefinition<TelegramFlow>[] => [
      {
        header: t('fields.enabled'),
        valueGetter: (item) => (
          <Switch
            checked={!!item.enabled}
            onChange={() => handleUpdateEnabled(item)}
          />
        ),
      },
      dataGridColumns.getIdColumn(),
      entityColumn,
      {
        header: t('fields.chat_id'),
        valueGetter: (item) => (
          <div className="tw-flex tw-items-center tw-gap-1">
            <span>{item.chatId}</span>
            {item.shopId && item.telegramUser && (
              <CheckTelegramChatMembershipIcon
                userId={item.telegramUser.id}
                chatId={item.chatId}
                isAuthenticated={item.telegramUser.isAuthenticated}
              />
            )}
          </div>
        ),
      },
      {
        header: t('fields.template'),
        valueKey: 'payinDisputeTemplate',
      },
      {
        header: t('fields.settings'),
        valueGetter: (item) => (
          <Fragment>
            {item.payinHideActions && (
              <div>{t('fields.payin_hide_actions')}</div>
            )}
          </Fragment>
        ),
        hidden: !(
          context === TelegramFlowContext.P2PProvider ||
          context === TelegramFlowContext.Trader
        ),
      },
      ...(additionalColumns || []),
    ],
    [entityColumn, handleUpdateEnabled, t, additionalColumns, context],
  );

  return (
    <CrudPage
      filters={{ filtersDefinitions }}
      table={{
        queryResult,
        columns,
        paginated: true,
        actions,
        ignoreState: { refetchLoading: true },
      }}
    />
  );
};
